import React from 'react';
import PropTypes from 'prop-types';
import { omit, each, replace, isDate, includes } from 'lodash';
import { Route, Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import {
  isWithPermissions,
  isTeacher,
  isPartnerAdmin,
} from '../utils/authScopes';
import { refreshAuth } from '../actions';
import { hasFeature, hasNewCommunicationDashboardFeature } from '../utils/features';

export const appNamedRoutes = {
  root: '/dashboard',
  notFound: '/dashboard/not-found',
  // auth related
  login: '/dashboard/login',
  sso: '/dashboard/sso/:partner_id',
  forgotPassword: '/dashboard/forgotpassword',
  resetPassword: '/dashboard/resetpassword',
  // pages
  asqs: '/dashboard/asqs',
  asq: '/dashboard/asqs/:player_id/questionnaire/:id',
  player: '/dashboard/asqs/player/:player_id',
  partnerRoot: '/dashboard/partner',
  accounts: '/dashboard/adults',
  account: '/dashboard/adults/:id',
  accountPlayers: '/dashboard/children',
  accountPlayer: '/dashboard/children/:id',
  groupcohortsReport: '/dashboard/reports/groupcohorts',
  groupcohortsReportGeneral: '/dashboard/reports/groupcohorts/general',
  groupcohortsReportOrganizationType: '/dashboard/reports/groupcohorts/organization-type',
  library: '/dashboard/library',
  cardsLibrary: '/dashboard/library/cards',
  programContentLibrary: '/dashboard/library/tips',
  settings: '/dashboard/settings',
  administration: '/dashboard/administration',
  administrationStaff: '/dashboard/administration/your-staff',
  administrationStaffNew: '/dashboard/administration/your-staff/new',
  administrationStaffEdit: '/dashboard/administration/your-staff/:id/edit',
  administrationOrganization: '/dashboard/administration/your-organization',
  administrationOrganizationSubgroupNew: '/dashboard/administration/your-organization/groups/:groupId/subgroups/new',
  administrationOrganizationSubgroupEdit: '/dashboard/administration/your-organization/subgroups/:id/edit',
  administrationOrganizationCohortNew: '/dashboard/administration/your-organization/subgroups/:subgroupId/cohorts/new',
  administrationOrganizationCohortEdit: '/dashboard/administration/your-organization/cohorts/:id/edit',
  administrationOrganizationGroupEdit: '/dashboard/administration/your-organization/groups/:id/edit',
  administrationFamiliesNew: '/dashboard/administration/your-families/new',
  administrationFamiliesEdit: '/dashboard/administration/your-families/:id/edit',
  administrationFamilies: '/dashboard/administration/your-families',
  communicationDashboard: '/dashboard/communication',
  communicationIndividuals: '/dashboard/communication/individuals',
  communicationGroups: '/dashboard/communication/groups',
};

const ALLOWED_ROUTES = ['root', 'partnerRoot', 'dashboard', 'settings'];
const TEACHER_ROUTES = ['asqs', 'asq', 'settings'];

export const linkWithParams = (routeType, params = {}) => {
  let path = appNamedRoutes[routeType];
  each(params, (v, k) => {
    path = replace(path, `:${k}`, v);
  });
  return path;
}

export const playerLink = (playerId, age, bankId, state = {}) => ({
  pathname: linkWithParams('player', { player_id: playerId }),
  search: `?age=${age}&bank=${bankId}`,
  state,
});

export const isValidAuth = (isAuthenticated, authExpires) => {
  let now = new Date();
  let dateExpires = new Date(authExpires || '');
  return isAuthenticated && (!isDate(dateExpires) || now <= dateExpires);
}

export const isPermitted = (type, auth) => {
  if ((isTeacher(auth) || isPartnerAdmin(auth)) && !includes(TEACHER_ROUTES, type)) return false;
  if ((type === 'communicationDashboard') && hasNewCommunicationDashboardFeature(auth)) return true;
  return isWithPermissions(auth) ? includes(auth.options, type) || includes(ALLOWED_ROUTES, type) : true;
}

class ProtectedRouteContainer extends React.Component {
  static propTypes = {
    auth: PropTypes.object.isRequired,
    component: PropTypes.any.isRequired,
    scope: PropTypes.object.isRequired,
    refreshAuth: PropTypes.func,
    location: PropTypes.object,
    partnerRequired: PropTypes.bool,
  }

  componentDidMount() {
    if (this.isAuthenticated()) {
      this.props.refreshAuth();
    }
  }

  render() {
    if (this.props.partnerRequired && !this.props.auth.partnerId) {
      return this.renderRedirect();
    }

    if (this.isAllowed()) {
      return this.renderRoute();
    }

    return this.renderRedirect();
  }

  isAllowed() {
    return this.isAuthenticated() && this.isRoleAllowed() && this.isFeatureAllowed();
  }

  isAuthenticated() {
    const { auth } = this.props;
    const { isAuthenticated, authExpires } = auth;

    return isValidAuth(isAuthenticated, authExpires);
  }

  isRoleAllowed() {
    const { scope, auth } = this.props;

    if (!scope.roles) {
      return true;
    }

    const roleAllowed = includes(scope.roles, auth.role);
    const typePermitted = isPermitted(scope.type, auth);

    return roleAllowed && typePermitted;
  }

  isFeatureAllowed() {
    const { scope, auth } = this.props;

    if (!scope.feature) {
      return true;
    }

    return hasFeature(auth, scope.feature);
  }

  renderRoute() {
    const { component: Component } = this.props;

    return (
      <Route
        {...this.renderProps()}
        render={props =>
          <Component {...props} />
        }
      />
    );
  }

  renderProps() {
    return omit(this.props, 'component', 'auth', 'scope');
  }

  renderRedirect() {
    return (
      <Route
        {...this.renderProps()}
        render = { () =>
          <Redirect to={this.notAllowedRedirectTo()} />
        }
      />
    );
  }

  notAllowedRedirectTo() {
    if (this.isAuthenticated()) {
      return this.authenticatedNotAllowedRedirectTo();
    }

    return this.unauthenticatedNotAllowedRedirectTo();
  }

  authenticatedNotAllowedRedirectTo() {
    if (!this.isAuthenticated()) return null;

    const { auth } = this.props;

    if (isTeacher(auth)) {
      return { pathname: appNamedRoutes.asqs };
    }

    return { pathname: appNamedRoutes.partnerRoot };
  }

  unauthenticatedNotAllowedRedirectTo() {
    return {
      pathname: appNamedRoutes.login,
      state: {
        from: this.props.location,
      },
    };
  }
}

const mapStateToProps = (state, ownProps) => ({
  auth: state.auth,
  scope: ownProps.scope,
});

const mapDispatchToProps = {
  refreshAuth,
};

const ProtectedRoute = connect(
  mapStateToProps,
  mapDispatchToProps,
)(ProtectedRouteContainer);

export default ProtectedRoute;
